import { createFileRoute } from "@tanstack/react-router";
import { Plus, Edit2, Trash2, Loader2, Sparkles, Wind } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { products as staticProducts, categories, fragrances } from "@/lib/products";
import { API_BASE_URL } from "@/lib/api";

export const Route = createFileRoute("/admin/products")({
  head: () => ({
    meta: [
      { title: "Manage Products — Royal Lumina" },
    ],
  }),
  component: AdminProducts,
});

type FormProduct = {
  id?: string;
  name: string;
  price: number;
  compareAt?: number;
  category: string;
  categoryId?: string;
  collectionId?: string;
  fragrance: string;
  topNotes?: string;
  heartNotes?: string;
  baseNotes?: string;
  scentDescription?: string;
  fragranceOptionsStr?: string;
  color: string;
  tag: string;
  inStock: boolean;
};

const emptyForm: FormProduct = {
  name: "",
  price: 0,
  compareAt: undefined,
  category: "",
  categoryId: "",
  collectionId: "",
  fragrance: "Floral",
  topNotes: "",
  heartNotes: "",
  baseNotes: "",
  scentDescription: "",
  fragranceOptionsStr: "",
  color: "gold",
  tag: "",
  inStock: true,
};

function AdminProducts() {
  const [products, setProducts] = useState<any[]>([]);
  const [dbCategories, setDbCategories] = useState<any[]>([]);
  const [dbCollections, setDbCollections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<FormProduct>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);

  const fetchCollections = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/collections`);
      if (res.ok) {
        const data = await res.json();
        setDbCollections(data);
      }
    } catch (err) {
      console.error("Failed to load collections in admin products page:", err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/categories`);
      if (res.ok) {
        const data = await res.json();
        setDbCategories(data);
      } else {
        setDbCategories(categories.map((c, i) => ({ id: `cat-${i}`, name: c })));
      }
    } catch (err) {
      setDbCategories(categories.map((c, i) => ({ id: `cat-${i}`, name: c })));
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/products`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      } else {
        setProducts(staticProducts);
      }
    } catch (err) {
      setProducts(staticProducts);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchCollections();
  }, []);

  const handleOpenCreate = () => {
    setFormData({
      ...emptyForm,
      categoryId: dbCategories[0]?.id || "",
      category: dbCategories[0]?.name || "Luxury",
      collectionId: dbCollections[0]?.id || "",
    });
    setEditingId(null);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (p: any) => {
    setFormData({
      name: p.name,
      price: p.price,
      compareAt: p.compareAt || undefined,
      category: p.category,
      categoryId: p.categoryId || "",
      collectionId: p.collectionId || "",
      fragrance: p.fragrance || "Floral",
      topNotes: p.fragranceNotes?.top || "",
      heartNotes: p.fragranceNotes?.heart || "",
      baseNotes: p.fragranceNotes?.base || "",
      scentDescription: p.scentDescription || "",
      fragranceOptionsStr: Array.isArray(p.fragranceOptions) ? p.fragranceOptions.join(", ") : "",
      color: p.color || "gold",
      tag: p.tag || "",
      inStock: p.inStock !== undefined ? p.inStock : true,
    });
    setEditingId(p.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this product?")) return;

    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const res = await fetch(`${API_BASE_URL}/api/products/${id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (res.ok) {
        toast.success("Product deleted successfully");
        fetchProducts();
      } else {
        setProducts((prev) => prev.filter((p) => p.id !== id));
        toast.success("Product removed (Local state)");
      }
    } catch (err) {
      setProducts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Product removed (Local state fallback)");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || formData.price <= 0) {
      toast.error("Please enter a valid product name and price");
      return;
    }

    const fragranceNotes = (formData.topNotes || formData.heartNotes || formData.baseNotes) ? {
      top: formData.topNotes || "",
      heart: formData.heartNotes || "",
      base: formData.baseNotes || "",
    } : undefined;

    const fragranceOptions = formData.fragranceOptionsStr
      ? formData.fragranceOptionsStr.split(",").map((s) => s.trim()).filter(Boolean)
      : undefined;

    const payload = {
      ...formData,
      img: "/placeholder.svg",
      fragranceNotes,
      scentDescription: formData.scentDescription,
      fragranceOptions,
    };

    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const url = editingId 
        ? `${API_BASE_URL}/api/products/${editingId}`
        : `${API_BASE_URL}/api/products`;
      
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(editingId ? "Product updated successfully" : "Product added successfully");
        setIsDialogOpen(false);
        fetchProducts();
      } else {
        throw new Error("API call failed");
      }
    } catch (err) {
      if (editingId) {
        setProducts((prev) => 
          prev.map((p) => p.id === editingId ? { ...p, ...payload } : p)
        );
        toast.success("Product updated (Local state simulation)");
      } else {
        const newProduct = {
          ...payload,
          id: `prod_${Date.now()}`,
          rating: 5.0,
          reviews: 0,
          img: "/placeholder.svg"
        };
        setProducts((prev) => [newProduct, ...prev]);
        toast.success("Product added (Local state simulation)");
      }
      setIsDialogOpen(false);
    }
  };

  const fragranceBadges: Record<string, string> = {
    Floral: "🌸 Floral",
    Woody: "🪵 Woody",
    Fresh: "🌿 Fresh",
    Sweet: "🍯 Sweet",
    Fruity: "🍊 Fruity",
  };

  return (
    <div className="space-y-8">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-display text-4xl tracking-tight text-primary">Catalog & Fragrance Management</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage candles, pricing, stock, scent notes and fragrance options.</p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="btn-gold self-start sm:self-auto"
        >
          <Plus className="h-4 w-4" />
          <span>Add Candle</span>
        </button>
      </div>

      {/* Products Table */}
      {loading ? (
        <div className="h-64 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="bg-card border border-border rounded-[24px] overflow-hidden shadow-luxe">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-muted/40 border-b border-border text-[0.65rem] uppercase tracking-wider text-muted-foreground font-semibold">
                  <th className="px-6 py-4">Image</th>
                  <th className="px-6 py-4">Candle Details</th>
                  <th className="px-6 py-4">Category</th>
                  <th className="px-6 py-4">Fragrance & Scent Profile</th>
                  <th className="px-6 py-4 text-right">Price</th>
                  <th className="px-6 py-4 text-center">Stock</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {products.map((p) => (
                  <tr key={p.id} className="hover:bg-muted/10 transition-colors">
                    <td className="px-6 py-4 shrink-0">
                      <div className="h-12 w-12 rounded-xl bg-card border border-border overflow-hidden grid place-items-center">
                        {p.img && p.img.startsWith("/") || p.img && p.img.startsWith("http") ? (
                          <img src={p.img} alt={p.name} className="h-full w-full object-cover" />
                        ) : (
                          <Sparkles className="h-5 w-5 text-gold/40 animate-pulse" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-primary">{p.name}</div>
                      {p.tag && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-gold/10 text-gold text-[0.6rem] uppercase tracking-widest font-semibold mt-1">
                          {p.tag}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">{p.category}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[0.65rem] font-semibold bg-gold/10 text-gold border border-gold/20">
                        {fragranceBadges[p.fragrance] || p.fragrance}
                      </span>
                      {p.fragranceNotes?.top && (
                        <div className="text-[0.7rem] text-muted-foreground mt-1 truncate max-w-[200px]" title={p.fragranceNotes.top}>
                          Top: {p.fragranceNotes.top}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-medium">
                      <div className="text-primary">₹{p.price.toLocaleString()}</div>
                      {p.compareAt && (
                        <div className="text-xs text-muted-foreground line-through">₹{p.compareAt.toLocaleString()}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[0.65rem] font-bold uppercase tracking-wider ${
                        p.inStock 
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
                          : "bg-rose-50 text-rose-700 border border-rose-200"
                      }`}>
                        {p.inStock ? "In Stock" : "Out of Stock"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenEdit(p)}
                          className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                          title="Edit Candle"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="p-2 rounded-lg hover:bg-rose-50 text-muted-foreground hover:text-rose-600 transition-colors"
                          title="Delete Candle"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Dialog */}
      {isDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-primary/40 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-[24px] max-w-xl w-full p-8 shadow-luxe relative overflow-y-auto max-h-[90vh]">
            <h3 className="font-display text-2xl text-primary mb-6">
              {editingId ? "Edit Candle & Fragrance Details" : "Create New Candle"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <label className="block">
                <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Product Name</span>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                />
              </label>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Price (₹)</span>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>
                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Compare Price (₹)</span>
                  <input
                    type="number"
                    min="1"
                    value={formData.compareAt || ""}
                    onChange={(e) => setFormData({ ...formData, compareAt: e.target.value ? Number(e.target.value) : undefined })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Category</span>
                  <select
                    value={formData.categoryId || formData.category}
                    onChange={(e) => {
                      const selectedVal = e.target.value;
                      const selectedCat = dbCategories.find(c => c.id === selectedVal || c.name === selectedVal);
                      setFormData({ 
                        ...formData, 
                        categoryId: selectedCat?.id || "", 
                        category: selectedCat?.name || selectedVal 
                      });
                    }}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  >
                    {dbCategories.map((c) => (
                      <option key={c.id || c.name} value={c.id || c.name}>{c.name}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Collection</span>
                  <select
                    value={formData.collectionId}
                    onChange={(e) => setFormData({ ...formData, collectionId: e.target.value })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  >
                    <option value="">No Collection</option>
                    {dbCollections.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </label>
              </div>

              {/* Fragrance Section */}
              <div className="p-4 rounded-[16px] bg-muted/20 border border-border space-y-4">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gold">
                  <Wind className="h-4 w-4" /> Fragrance & Scent Profile (Khushboo Details)
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Fragrance Family</span>
                    <select
                      value={formData.fragrance}
                      onChange={(e) => setFormData({ ...formData, fragrance: e.target.value })}
                      className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                    >
                      {fragrances.map((f) => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Top Notes (Pehli Mehak)</span>
                    <input
                      type="text"
                      placeholder="e.g. Bulgarian Rose & Peony"
                      value={formData.topNotes}
                      onChange={(e) => setFormData({ ...formData, topNotes: e.target.value })}
                      className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                    />
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Heart Notes (Main Scent)</span>
                    <input
                      type="text"
                      placeholder="e.g. Honeycomb & Damask Rose"
                      value={formData.heartNotes}
                      onChange={(e) => setFormData({ ...formData, heartNotes: e.target.value })}
                      className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Base Notes (Aura & Lingering)</span>
                    <input
                      type="text"
                      placeholder="e.g. Warm Amber & Sandalwood"
                      value={formData.baseNotes}
                      onChange={(e) => setFormData({ ...formData, baseNotes: e.target.value })}
                      className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Scent Experience Story (Hindi/English Description)</span>
                  <textarea
                    rows={2}
                    placeholder="Describe how the candle smells when lit (e.g. Iss candle ko jalate hi taaza gulaab aur ambar ki garmahat phailti hai...)"
                    value={formData.scentDescription}
                    onChange={(e) => setFormData({ ...formData, scentDescription: e.target.value })}
                    className="mt-2 w-full p-3 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>

                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Selectable Fragrance Options (Comma-separated for user selection)</span>
                  <input
                    type="text"
                    placeholder="e.g. Velvet Rose & Honey, Rose & Peony, Smoked Rose & Oud"
                    value={formData.fragranceOptionsStr}
                    onChange={(e) => setFormData({ ...formData, fragranceOptionsStr: e.target.value })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Color Palette</span>
                  <select
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  >
                    <option value="gold">Gold</option>
                    <option value="cream">Cream</option>
                    <option value="green">Green</option>
                    <option value="rose">Rose</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Badge Tag</span>
                  <input
                    type="text"
                    placeholder="e.g. Bestseller, New"
                    value={formData.tag}
                    onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>
              </div>

              <div className="flex items-center gap-3 py-2">
                <input
                  type="checkbox"
                  id="inStock"
                  checked={formData.inStock}
                  onChange={(e) => setFormData({ ...formData, inStock: e.target.checked })}
                  className="h-4 w-4 border-border rounded text-gold focus:ring-gold"
                />
                <label htmlFor="inStock" className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  This product is in stock
                </label>
              </div>

              <div className="mt-8 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsDialogOpen(false)}
                  className="btn-outline-dark h-11 py-0 px-5 text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-gold h-11 py-0 px-6 text-xs"
                >
                  Save Candle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
